import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {MenuItem, Select, CircularProgress} from '@mui/material';
import {CoverPageCard} from '../components/CoverPageCard';
import {Framework} from '../components/Framework';
import {sendGAPageview, fetchPostAuthSafe} from '../app/utils';
import {checkAuthentication, getCustomerAndUserInfo, getUserAccountInfo, setIsUserLanguageReloading} from '../appSlice';

const languageFlags = {
  en: '/img/usa-flag-circle.png',
  es: '/img/mexico-flag-circle.png',
};

// English shows the UK flag instead of the US flag for users whose units are set to metric
function getLanguageFlags(unitsLength) {
  return unitsLength === 'metric' ? {...languageFlags, en: '/img/uk-flag-circle.png'} : languageFlags;
}

function Error() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {t, i18n} = useTranslation();

  const authenticationStatus = useSelector((state) => {
    return state.app.authenticationStatus;
  });
  const userSettings = useSelector((state) => {
    return state.app.userSettings;
  });
  const customerSettings = useSelector((state) => {
    return state.app.customerSettings;
  });

  const isUserLanguageReloading = useSelector((state) => {
    return state.app.isUserLanguageReloading;
  });

  const [initDone, setInitDone] = useState(false);
  const [userLoaded, setUserLoaded] = useState(false);
  const [localLanguage, setLocalLanguage] = useState(i18n.language || 'en');

  const flags = getLanguageFlags(userSettings.general?.unitsLength);

  useEffect(() => {
    (async () => {
      await dispatch(checkAuthentication());
      setInitDone(true);
    })();
  }, []);

  useEffect(() => {
    if (!initDone) {
      return;
    }
    if (!authenticationStatus.loggedIn) {
      setUserLoaded(true);
      return;
    }
    if (userSettings?.username) {
      setUserLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      await dispatch(getCustomerAndUserInfo());
      if (!cancelled) {
        setUserLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initDone, authenticationStatus.loggedIn, userSettings?.username]);

  useEffect(() => {
    document.title = 'IntelliCulture | Error';
    sendGAPageview(document.title, true);
  }, []);

  let errorMsg = t('Page Not Found');

  if (location.state && location.state.errorMsg && location.state.errorMsg == 'outOfSync') {
    errorMsg = t('Warning - Client data out of sync. You will now be logged out. Please log in again.');
  } else if (location.state && location.state.noAccessiblePages) {
    const organizationName = customerSettings?.general?.organizationName;
    errorMsg = organizationName ? t('Welcome {{organizationName}}', {organizationName}) : t('Welcome');
  } else if (location.state && location.state.errorMsg) {
    errorMsg = location.state.errorMsg;
  }

  const showLoggedInChrome = initDone && authenticationStatus.loggedIn && userLoaded && userSettings?.username;
  const showLoader = !initDone || (authenticationStatus.loggedIn && !userLoaded);
  const currentLanguage = userSettings?.language || localLanguage;

  useEffect(() => {
    if (userSettings?.language) {
      setLocalLanguage(userSettings.language);
    }
  }, [userSettings?.language]);

  async function handleChangeLanguage(newLanguage) {
    if (currentLanguage == newLanguage) {
      return;
    }
    if (authenticationStatus.loggedIn && userSettings?.username && userSettings?.databaseName) {
      dispatch(setIsUserLanguageReloading(true));
      await fetchPostAuthSafe(
        '/settings/updateUserLanguage',
        {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({language: newLanguage}),
        },
        userSettings.username,
        userSettings.databaseName
      );
      dispatch(getUserAccountInfo());
      return;
    }
    setLocalLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  }

  function languageSelect() {
    if (isUserLanguageReloading) {
      return <CircularProgress size={24} />;
    }
    return (
      <Select
        title={t('Language')}
        size='small'
        value={currentLanguage}
        onChange={(event) => {
          return handleChangeLanguage(event.target.value);
        }}
        renderValue={(selected) => {
          return (
            <span>
              <img className='mb-1' src={flags[selected]} style={{width: 24, height: 24}} alt='' />
              {` ${selected.toUpperCase()}`}
            </span>
          );
        }}
        sx={{
          '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: 'none',
          },
        }}
      >
        <MenuItem value='en'>
          <span>
            <img src={flags['en']} style={{width: 24, height: 24}} alt='' /> English
          </span>
        </MenuItem>
        <MenuItem value='es'>
          <span>
            <img src={flags['es']} style={{width: 24, height: 24}} alt='' /> Español
          </span>
        </MenuItem>
      </Select>
    );
  }

  if (showLoader) {
    return (
      <CoverPageCard>
        <div style={{height: '150px'}}>
          <img src='/img/Logo_Text_NoSlogan.png' className='d-block mx-auto w-auto img-fluid' />
        </div>
        <div className='loader mx-auto'></div>
      </CoverPageCard>
    );
  }

  function handleHomeClick() {
    if (
      window.hasOwnProperty('ReactNativeWebView') &&
      window.ReactNativeWebView &&
      userSettings?.username &&
      customerSettings?.name
    ) {
      // Switch to native Scan immediately — do not await telemetry POST.
      try {
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            url: '/',
            data: JSON.stringify({
              redirect: 'scan',
              staleVersion: userSettings.staleVersion,
              username: userSettings.username,
              customerName: customerSettings.name,
            }),
          })
        );
      } catch (err) {
        console.error('Failed to post scan redirect to native shell', err);
      }
      void fetchPostAuthSafe(
        '/scanLinkClicked',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'sessionsyncusername': userSettings.username,
            'sessionsyncdb': customerSettings.name,
          },
          body: JSON.stringify({value: true}),
        },
        userSettings.username,
        customerSettings.name
      );
      return;
    }
    navigate('/');
  }

  const innerContent = (
    <>
      <div className='text-center'>
        <a href='https://www.intelliculture.ca/' target='_blank' className='card-logo' rel='noreferrer'>
          <img src='/img/Logo_Text_NoSlogan.png' alt='Logo' border='0' width='256' className='card-logo-img' />
        </a>
        <h1 id='heading' className='h4 text-gray-900 mb-4'>
          {errorMsg}
        </h1>
      </div>
      <hr />
      {!showLoggedInChrome && <div className='text-center mb-3'>{languageSelect()}</div>}
      <div className='text-center'>
        <button type='button' className='btn border bg-white small' onClick={handleHomeClick}>
          {t('Home')}
        </button>
      </div>
    </>
  );

  if (showLoggedInChrome) {
    return (
      <Framework activePage='error' pageName='' topBarSearch={false}>
        <div className='tab-wrapper'>
          <CoverPageCard>{innerContent}</CoverPageCard>
        </div>
      </Framework>
    );
  }

  return <CoverPageCard>{innerContent}</CoverPageCard>;
}

export {Error};
